/**
 * Reports Service
 * Handles API calls for credential reports
 */

import { supabase } from "@/supabase.config";
import { DatabaseError } from "@/lib/errors";
import type {
  MinisterInRequest,
  MinistersListReportFilters,
  ActiveMinister,
  MinisterRegistryEntry,
  MinisterTransferEntry,
  MinisterTransferFilters,
  MinisterDegreeChangeEntry,
  MinisterDegreeChangeFilters,
} from "../types";


// Hard cap: maximum rows the view query will return in a single call.
// 3,474 crm rows exist in prod (all in 2026). Year-scoped queries are always
// well under this cap. Raising it is safe up to ~10k, but never remove it —
// an unbounded query on this 9-JOIN view will OOM the free-tier instance.
const REPORT_ROW_LIMIT = 5000;

/**
 * Fetch ministers in credential requests with filters.
 * Uses vw_credential_request_ministers_extended view.
 *
 * IMPORTANT — no OFFSET batching:
 * Each OFFSET call forces Postgres to re-execute the full 9-JOIN scan from
 * scratch (views have no cursor). Four batches of 1k rows = four full-table
 * re-scans in quick succession, which OOMs the Supabase free-tier instance.
 * Instead we issue a single query with a year-scoped filter and a hard row
 * cap. Current prod data (3,474 crm rows, one year) fits comfortably.
 *
 * Safety guard: if neither dateFrom nor dateTo is provided, the query is
 * automatically scoped to the current year to prevent unbounded scans.
 */
export async function getMinistersInRequests(
  filters: MinistersListReportFilters
): Promise<MinisterInRequest[]> {
  try {
    const currentYear = new Date().getFullYear();

    // Derive a year scope from the date filters. Prefer request_year (indexed
    // on credential_requests.year) over created_at (no index on that column).
    const yearFromDate = filters.dateFrom
      ? parseInt(filters.dateFrom.substring(0, 4), 10)
      : null;
    const yearToDate = filters.dateTo
      ? parseInt(filters.dateTo.substring(0, 4), 10)
      : null;

    let query = supabase
      .from("vw_credential_request_ministers_extended")
      .select("*");

    // Filter by year range using the indexed request_year column.
    // Falls back to current year when no date filter is supplied.
    const effectiveYearFrom = yearFromDate ?? currentYear;
    const effectiveYearTo   = yearToDate   ?? currentYear;
    if (effectiveYearFrom === effectiveYearTo) {
      query = query.eq("request_year", effectiveYearFrom);
    } else {
      query = query
        .gte("request_year", effectiveYearFrom)
        .lte("request_year", effectiveYearTo);
    }

    if (filters.credentialTypes?.length)
      query = query.in("credential_type", filters.credentialTypes);
    if (filters.statuses?.length)
      query = query.in("request_status", filters.statuses);
    if (filters.districtId)
      query = query.eq("request_district_id", filters.districtId);
    if (filters.regionId)
      query = query.eq("request_region_id", filters.regionId);
    if (filters.degreeId)
      query = query.eq("degree_id", filters.degreeId);
    if (filters.search?.trim()) {
      const s = filters.search.trim();
      query = query.or(
        `minister_name.ilike.%${s}%,dni.ilike.%${s}%,adb_code.ilike.%${s}%`
      );
    }

    query = query
      .order("request_year", { ascending: false })
      .order("request_code", { ascending: true })
      .order("minister_name", { ascending: true })
      .limit(REPORT_ROW_LIMIT);

    const { data, error } = await query;
    if (error) throw error;

    const result = (data as unknown as MinisterInRequest[]) ?? [];

    if (result.length >= REPORT_ROW_LIMIT) {
      console.warn(
        `getMinistersInRequests: hit the ${REPORT_ROW_LIMIT}-row cap. ` +
        "Results may be incomplete. Consider narrowing the year or territory filter."
      );
    }

    return result;
  } catch (error) {
    console.error("Error fetching ministers in requests:", error);
    throw new DatabaseError("Failed to fetch ministers in requests", error);
  }
}

/**
 * System-wide active minister count grouped by degree
 */
export interface MinisterSystemDegreeCount {
  degreeId: string | null;
  degreeName: string;
  totalActive: number;
}

/**
 * Fetch active minister counts grouped by degree (system-wide).
 * Used for comparison against ministers-in-requests in the reports view.
 *
 * Strategy:
 * - Fetch degree names in a single small query (≤ 20 rows)
 * - Fetch minister degree_ids in batches (minimal columns, no JOINs)
 * - Both run in parallel for the first batch; subsequent minister batches run
 *   sequentially until exhausted.
 * This is significantly faster than fetching full minister rows with JOIN.
 */
export async function getActiveMinisterCountByDegree(): Promise<MinisterSystemDegreeCount[]> {
  try {
    const BATCH_SIZE = 1000;

    // Run the degrees lookup and first minister batch in parallel
    const [degreesResult, firstBatch] = await Promise.all([
      supabase.from("degrees").select("id, name").eq("is_active", true),
      supabase
        .from("ministers")
        .select("degree_id")         // Minimal column — no JOIN needed
        .eq("is_active", true)
        .range(0, BATCH_SIZE - 1),
    ]);

    if (degreesResult.error) throw degreesResult.error;
    if (firstBatch.error) throw firstBatch.error;

    // Build degree name lookup map (id → name)
    const degreeNameMap = new Map<string, string>();
    (degreesResult.data || []).forEach((d: { id: string; name: string }) => {
      degreeNameMap.set(d.id, d.name);
    });

    // Collect all degree_id results, starting with the first batch
    type DegreeIdRow = { degree_id: string | null };
    const allRows: DegreeIdRow[] = [...((firstBatch.data as DegreeIdRow[]) ?? [])];

    // Fetch remaining batches if needed
    if (allRows.length === BATCH_SIZE) {
      let offset = BATCH_SIZE;
      while (true) {
        const { data, error } = await supabase
          .from("ministers")
          .select("degree_id")
          .eq("is_active", true)
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) throw error;

        const batch = (data as DegreeIdRow[]) ?? [];
        allRows.push(...batch);

        if (batch.length < BATCH_SIZE) break;
        offset += BATCH_SIZE;
        if (offset > 50000) {
          console.warn("getActiveMinisterCountByDegree: reached 50k safety limit");
          break;
        }
      }
    }

    // Count by degree_id in JS
    const countMap = new Map<string, number>();
    allRows.forEach(({ degree_id }) => {
      const key = degree_id ?? "__none__";
      countMap.set(key, (countMap.get(key) ?? 0) + 1);
    });

    // Map to final shape using the degree name lookup
    const result: MinisterSystemDegreeCount[] = Array.from(countMap.entries()).map(
      ([key, totalActive]) => ({
        degreeId: key === "__none__" ? null : key,
        degreeName: degreeNameMap.get(key) ?? "Sin Grado",
        totalActive,
      })
    );

    return result.sort((a, b) => b.totalActive - a.totalActive);
  } catch (error) {
    console.error("Error fetching active minister count by degree:", error);
    throw new DatabaseError("Failed to fetch active minister count by degree", error);
  }
}

/**
 * Get available years for credential requests
 */
export async function getAvailableYears(): Promise<number[]> {
  try {
    const { data, error } = await supabase
      .from("credential_requests")
      .select("year")
      .not("year", "is", null)
      .order("year", { ascending: false })
      .limit(50); // Years are a small bounded set; cap to avoid unbounded fetch

    if (error) throw error;
    if (!data) return [];

    // Get unique years
    const years = [...new Set(data.map((r: { year: number | null }) => r.year).filter((y): y is number => y !== null))];
    return years;
  } catch (error) {
    console.error("Error fetching available years:", error);
    throw new DatabaseError("Failed to fetch available years", error);
  }
}

/**
 * Fetch all active ministers with degree, district, and region details.
 * Used to compute which ministers are NOT in any current credential request.
 *
 * Uses OFFSET batching — safe here because this is a direct table query
 * (not a view), so Postgres uses the is_active index and doesn't re-scan.
 */
export async function getActiveMinisters(): Promise<ActiveMinister[]> {
  try {
    const BATCH_SIZE = 1000;
    const all: ActiveMinister[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await supabase
        .from("ministers")
        .select(`
          id, full_name, first_name, last_name, second_last_name, marriage_last_name,
          dni, adb_code, email, cell_phone, position, degree_id, district_id, region_id,
          degree:degrees(name),
          district:districts!ministers_district_id_fkey(name, code),
          region:regions!ministers_region_id_fkey(name, code)
        `)
        .eq("is_active", true)
        .order("last_name", { ascending: true })
        .order("first_name", { ascending: true })
        .range(offset, offset + BATCH_SIZE - 1);

      if (error) throw error;

      const batch = (data as unknown as ActiveMinister[]) ?? [];
      all.push(...batch);

      if (batch.length < BATCH_SIZE) break;
      offset += BATCH_SIZE;
      if (offset > 50000) {
        console.warn("getActiveMinisters: reached 50k safety limit");
        break;
      }
    }

    return all;
  } catch (error) {
    console.error("Error fetching active ministers:", error);
    throw new DatabaseError("Failed to fetch active ministers", error);
  }
}

// ─── Padrón General ──────────────────────────────────────────────────────────

/**
 * Fetch all ministers (active + inactive) with degree/district/region/inactive_reason.
 * Credential flag is computed client-side after a separate year-scoped query.
 */
export async function getAllMinisters(): Promise<
  Omit<MinisterRegistryEntry, 'latest_status_note' | 'missing_from_credentials'>[]
> {
  try {
    const BATCH_SIZE = 1000;
    type Row = Omit<MinisterRegistryEntry, 'latest_status_note' | 'missing_from_credentials'>;
    const all: Row[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await supabase
        .from("ministers")
        .select(`
          id, full_name, adb_code, dni,
          is_active, inactive_reason_code, inactive_at,
          degree_id, district_id, region_id,
          degree:degrees(name),
          district:districts!ministers_district_id_fkey(name),
          region:regions!ministers_region_id_fkey(name),
          inactive_reason:inactive_reasons!ministers_inactive_reason_code_fkey(code, label)
        `)
        .order("last_name", { ascending: true })
        .order("first_name", { ascending: true })
        .range(offset, offset + BATCH_SIZE - 1);

      if (error) throw error;

      type RawRow = {
        id: string;
        full_name: string;
        adb_code: string | null;
        dni: string | null;
        is_active: boolean;
        inactive_reason_code: string | null;
        inactive_at: string | null;
        degree_id: string | null;
        district_id: string | null;
        region_id: string | null;
        degree: { name: string } | null;
        district: { name: string } | null;
        region: { name: string } | null;
        inactive_reason: { code: string; label: string } | null;
      };

      const batch = ((data as unknown as RawRow[]) ?? []).map((r) => ({
        id: r.id,
        full_name: r.full_name,
        adb_code: r.adb_code,
        dni: r.dni,
        is_active: r.is_active,
        inactive_reason_code: r.inactive_reason_code,
        inactive_reason_label: r.inactive_reason?.label ?? null,
        inactive_at: r.inactive_at,
        degree_id: r.degree_id,
        degree_name: r.degree?.name ?? null,
        district_id: r.district_id,
        district_name: r.district?.name ?? null,
        region_id: r.region_id,
        region_name: r.region?.name ?? null,
      }));

      all.push(...batch);
      if (batch.length < BATCH_SIZE) break;
      offset += BATCH_SIZE;
      if (offset > 50000) {
        console.warn("getAllMinisters: reached 50k safety limit");
        break;
      }
    }

    return all;
  } catch (error) {
    console.error("Error fetching all ministers:", error);
    throw new DatabaseError("Failed to fetch all ministers", error);
  }
}

/**
 * Fetch minister IDs present in any active credential request for the given year.
 * "Active" = any status except DRAFT and REJECTED.
 */
export async function getMinisterIdsInActiveRequests(year: number): Promise<Set<string>> {
  try {
    // Step 1: get active request IDs for year
    const { data: requests, error: reqError } = await supabase
      .from("credential_requests")
      .select("id")
      .eq("year", year)
      .in("status", ["SUBMITTED", "APPROVED", "PRINTED", "DELIVERED"]);

    if (reqError) throw reqError;
    if (!requests?.length) return new Set();

    const requestIds = requests.map((r: { id: string }) => r.id);

    // Step 2: get minister IDs in those requests (batch if many requests)
    const BATCH = 200;
    const ministerIds = new Set<string>();
    for (let i = 0; i < requestIds.length; i += BATCH) {
      const { data, error } = await supabase
        .from("credential_request_ministers")
        .select("minister_id")
        .in("credential_request_id", requestIds.slice(i, i + BATCH));

      if (error) throw error;
      (data ?? []).forEach((r: { minister_id: string | null }) => { if (r.minister_id) ministerIds.add(r.minister_id); });
    }

    return ministerIds;
  } catch (error) {
    console.error("Error fetching minister IDs in active requests:", error);
    throw new DatabaseError("Failed to fetch minister IDs in active requests", error);
  }
}

/**
 * Fetch latest status_change notes per minister from minister_history.
 * Returns a map of minister_id → latest note (null if none).
 */
export async function getLatestStatusNotesByMinister(): Promise<Map<string, string>> {
  try {
    const { data, error } = await supabase
      .from("minister_history")
      .select("minister_id, notes, event_date")
      .eq("event_type", "status_change")
      .not("notes", "is", null)
      .order("event_date", { ascending: false })
      .limit(5000);

    if (error) throw error;

    const noteMap = new Map<string, string>();
    (data ?? []).forEach((r: { minister_id: string; notes: string | null }) => {
      // First occurrence per minister = most recent (ordered DESC)
      if (r.notes && !noteMap.has(r.minister_id)) {
        noteMap.set(r.minister_id, r.notes);
      }
    });

    return noteMap;
  } catch (error) {
    console.error("Error fetching status notes:", error);
    throw new DatabaseError("Failed to fetch status notes", error);
  }
}

// ─── Transfers ───────────────────────────────────────────────────────────────

const HISTORY_ROW_LIMIT = 5000;

/**
 * Fetch minister transfer history with filters.
 */
export async function getMinisterTransfers(
  filters: MinisterTransferFilters
): Promise<MinisterTransferEntry[]> {
  try {
    let query = supabase
      .from("minister_history")
      .select(`
        id, minister_id, event_date, notes,
        district_from, district_to, region_from, region_to,
        minister:ministers!minister_history_minister_id_fkey(
          full_name, adb_code,
          district:districts!ministers_district_id_fkey(name),
          region:regions!ministers_region_id_fkey(name)
        ),
        district_from_rel:districts!minister_history_district_from_fkey(name),
        district_to_rel:districts!minister_history_district_to_fkey(name),
        region_from_rel:regions!minister_history_region_from_fkey(name),
        region_to_rel:regions!minister_history_region_to_fkey(name)
      `)
      .eq("event_type", "transfer")
      .gte("event_date", `${filters.dateFrom}T00:00:00`)
      .lte("event_date", `${filters.dateTo}T23:59:59`);

    query = query
      .order("event_date", { ascending: false })
      .limit(HISTORY_ROW_LIMIT);

    const { data, error } = await query;
    if (error) throw error;

    type RawTransfer = {
      id: string;
      minister_id: string;
      event_date: string;
      notes: string | null;
      district_from: string | null;
      district_to: string | null;
      region_from: string | null;
      region_to: string | null;
      minister: { full_name: string; adb_code: string | null; district: { name: string } | null; region: { name: string } | null } | null;
      district_from_rel: { name: string } | null;
      district_to_rel: { name: string } | null;
      region_from_rel: { name: string } | null;
      region_to_rel: { name: string } | null;
    };

    const rows = (data as unknown as RawTransfer[]) ?? [];

    return rows.map((r) => ({
      id: r.id,
      minister_id: r.minister_id,
      minister_name: r.minister?.full_name ?? "—",
      adb_code: r.minister?.adb_code ?? null,
      event_date: r.event_date,
      notes: r.notes,
      district_from_id: r.district_from,
      district_from_name: r.district_from_rel?.name ?? null,
      district_to_id: r.district_to,
      district_to_name: r.district_to_rel?.name ?? null,
      region_from_id: r.region_from,
      region_from_name: r.region_from_rel?.name ?? null,
      region_to_id: r.region_to,
      region_to_name: r.region_to_rel?.name ?? null,
      current_district_name: r.minister?.district?.name ?? null,
      current_region_name: r.minister?.region?.name ?? null,
    }));
  } catch (error) {
    console.error("Error fetching minister transfers:", error);
    throw new DatabaseError("Failed to fetch minister transfers", error);
  }
}

// ─── Degree Changes ───────────────────────────────────────────────────────────

/**
 * Fetch minister degree change history with filters.
 */
export async function getMinisterDegreeChanges(
  filters: MinisterDegreeChangeFilters
): Promise<MinisterDegreeChangeEntry[]> {
  try {
    let query = supabase
      .from("minister_history")
      .select(`
        id, minister_id, event_date, notes,
        degree_from, degree_to,
        minister:ministers!minister_history_minister_id_fkey(
          full_name, adb_code,
          district:districts!ministers_district_id_fkey(name),
          region:regions!ministers_region_id_fkey(name)
        ),
        degree_from_rel:degrees!minister_history_degree_from_fkey(name),
        degree_to_rel:degrees!minister_history_degree_to_fkey(name)
      `)
      .eq("event_type", "degree_update")
      .gte("event_date", `${filters.dateFrom}T00:00:00`)
      .lte("event_date", `${filters.dateTo}T23:59:59`)
      .order("event_date", { ascending: false })
      .limit(HISTORY_ROW_LIMIT);

    const { data, error } = await query;
    if (error) throw error;

    type RawDegreeChange = {
      id: string;
      minister_id: string;
      event_date: string;
      notes: string | null;
      degree_from: string | null;
      degree_to: string | null;
      minister: { full_name: string; adb_code: string | null; district: { name: string } | null; region: { name: string } | null } | null;
      degree_from_rel: { name: string } | null;
      degree_to_rel: { name: string } | null;
    };

    const rows = (data as unknown as RawDegreeChange[]) ?? [];

    return rows.map((r) => ({
      id: r.id,
      minister_id: r.minister_id,
      minister_name: r.minister?.full_name ?? "—",
      adb_code: r.minister?.adb_code ?? null,
      event_date: r.event_date,
      notes: r.notes,
      degree_from_id: r.degree_from,
      degree_from_name: r.degree_from_rel?.name ?? null,
      degree_to_id: r.degree_to,
      degree_to_name: r.degree_to_rel?.name ?? null,
      current_district_name: r.minister?.district?.name ?? null,
      current_region_name: r.minister?.region?.name ?? null,
    }));
  } catch (error) {
    console.error("Error fetching minister degree changes:", error);
    throw new DatabaseError("Failed to fetch minister degree changes", error);
  }
}
